import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { hasAdminConsoleAccess } from './aivisRoles';
import './ai-vis.css';

const LOOKER_CONNECTOR_URL = process.env.REACT_APP_LOOKER_STUDIO_CONNECTOR_URL || '';
const LOOKER_DOCS_URL = '/docs/ai-vis/looker-studio';

/* ─── helpers ─────────────────────────────────────────────────── */

function displayHostname(url) {
  if (!url) return '';
  try {
    const u = new URL(url.startsWith('http') ? url : `https://${url}`);
    return u.hostname.replace(/^www\./i, '');
  } catch {
    return url;
  }
}

function DataStudioIcon({ size = 18 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="3" width="7" height="7" rx="2" />
      <rect x="14" y="3" width="7" height="7" rx="2" />
      <rect x="3" y="14" width="7" height="7" rx="2" />
      <rect x="14" y="14" width="7" height="7" rx="2" />
      <path d="M10 6.5h4M6.5 10v4M17.5 10v4M10 17.5h4" />
    </svg>
  );
}

/** Stable pastel tint from org name for avatar background. */
function orgTint(name) {
  let h = 2166136261;
  for (let i = 0; i < (name || '').length; i++) {
    h ^= (name || '').charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const hue = Math.abs(h) % 360;
  return `hsl(${hue} 45% 90%)`;
}

/**
 * Ordered logo sources for an org card: backend-stored logo_url (resolved
 * server-side, same as client_logo_url for campaigns) → local fallback for
 * the two built-in orgs.
 */
function orgCardLogoSources(name, logoUrl) {
  const sources = [];
  if (logoUrl) sources.push(logoUrl);
  const n = (name || '').toLowerCase();
  if (n.includes('visibility labs')) sources.push('/visibilitylabs-mark.png');
  if (n.includes('180 marketing') || n.includes('180marketing')) sources.push('/Site-logo-180.png');
  return sources;
}

function OrgAvatar({ org }) {
  const sources = orgCardLogoSources(org.name, org.logo_url);
  const [idx, setIdx] = useState(0);
  const letter = (org.name || '?').slice(0, 1).toUpperCase();
  const bg = orgTint(org.name);

  // Reset to first source when org changes
  useEffect(() => { setIdx(0); }, [org.name, org.logo_url]);

  if (idx < sources.length) {
    return (
      <img
        src={sources[idx]}
        alt=""
        className="aivis-org-card-logo"
        onError={() => setIdx((i) => i + 1)}
      />
    );
  }
  return (
    <span className="aivis-org-card-logo-fallback" style={{ background: bg }} aria-hidden>
      {letter}
    </span>
  );
}

/* ─── Add / Edit modal ────────────────────────────────────────── */

function OrgFormModal({ open, org, onClose, onSaved, busy, setBusy }) {
  const isEdit = Boolean(org);
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open) {
      setName(org?.name || '');
      setWebsite(org?.website || '');
      setErr('');
    }
  }, [open, org]);

  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  async function handleSubmit(e) {
    e.preventDefault();
    const n = name.trim();
    if (!n) { setErr('Name is required.'); return; }
    setErr('');
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/aivis/organizations/${org.id}/`, { name: n, website: website.trim() });
      } else {
        await api.post('/aivis/organizations/', { name: n, website: website.trim() });
      }
      await onSaved();
      onClose();
    } catch (ex) {
      const d = ex.response?.data;
      setErr(d?.name?.[0] || d?.detail || 'Could not save organization.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="aivis-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="aivis-org-modal-title"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="aivis-modal aivis-org-modal">
        <h2 id="aivis-org-modal-title" className="aivis-org-modal-title">
          {isEdit ? 'Edit organization' : 'New organization'}
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="aivis-field">
            <label htmlFor="aivis-org-name">Name <span aria-hidden style={{ color: 'var(--aivis-danger)' }}>*</span></label>
            <input
              id="aivis-org-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Visibility Labs"
              autoFocus
              required
            />
          </div>
          <div className="aivis-field">
            <label htmlFor="aivis-org-website">Website</label>
            <input
              id="aivis-org-website"
              type="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://example.com"
            />
            {website && (
              <span className="aivis-org-logo-preview-label" style={{ marginTop: 4, display: 'block', fontSize: 12, opacity: 0.6 }}>
                Logo will be auto-fetched from this website on save
              </span>
            )}
          </div>
          {err && <div className="aivis-auth-error" style={{ marginBottom: 12 }}>{err}</div>}
          <div className="aivis-wizard-actions" style={{ marginTop: 20 }}>
            <button type="button" className="aivis-btn-secondary" disabled={busy} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="aivis-btn-primary" style={{ width: 'auto' }} disabled={busy}>
              {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Delete confirmation modal ───────────────────────────────── */

function OrgDeleteModal({ org, orgs, onClose, onDeleted, busy, setBusy }) {
  const hasClients = (org?.client_count || 0) > 0;
  const [deleteMode, setDeleteMode] = useState('transfer'); // 'transfer' | 'delete_clients'
  const [transferTo, setTransferTo] = useState('');
  const [err, setErr] = useState('');

  // Available transfer targets (exclude this org)
  const targets = useMemo(() => (orgs || []).filter((o) => o.id !== org?.id), [orgs, org]);

  useEffect(() => {
    setDeleteMode('transfer');
    setTransferTo(targets[0]?.id || '');
    setErr('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org?.id]);

  useEffect(() => {
    if (!org) return undefined;
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [org, onClose]);

  if (!org) return null;

  async function handleDelete() {
    setErr('');
    setBusy(true);
    try {
      let params = '';
      if (hasClients) {
        if (deleteMode === 'transfer') {
          if (!transferTo) { setErr('Please select a target organization.'); setBusy(false); return; }
          params = `?action=transfer&transfer_to=${transferTo}`;
        } else {
          params = `?action=delete_clients`;
        }
      }
      await api.delete(`/aivis/organizations/${org.id}/${params}`);
      await onDeleted();
      onClose();
    } catch (ex) {
      const d = ex.response?.data;
      setErr(d?.detail || 'Could not delete organization.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="aivis-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="aivis-org-del-title"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="aivis-modal aivis-org-modal">
        <div className="aivis-org-del-header">
          <div className="aivis-org-del-icon" aria-hidden>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <line x1="12" y1="9" x2="12" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <line x1="12" y1="17" x2="12.01" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>

          <h2 id="aivis-org-del-title" className="aivis-org-modal-title">
            Delete "{org.name}"?
          </h2>
        </div>

        {!hasClients ? (
          <p className="aivis-modal-body">
            This organization has no clients. It will be permanently removed.
          </p>
        ) : (
          <>
            <p className="aivis-modal-body">
              This organization has <strong>{org.client_count} client{org.client_count === 1 ? '' : 's'}</strong>.
              Choose what to do with them before deleting:
            </p>

            <div className="aivis-org-del-options">
              <label className={`aivis-org-del-option ${deleteMode === 'transfer' ? 'aivis-org-del-option--active' : ''}`}>
                <input
                  type="radio"
                  name="org-del-mode"
                  value="transfer"
                  checked={deleteMode === 'transfer'}
                  onChange={() => setDeleteMode('transfer')}
                />
                <div className="aivis-org-del-option-body">
                  <span className="aivis-org-del-option-title">Transfer clients</span>
                  <span className="aivis-org-del-option-desc">Move all clients to another organization. Their data is preserved.</span>
                  {deleteMode === 'transfer' && (
                    <select
                      className="aivis-org-del-select"
                      value={transferTo}
                      onChange={(e) => setTransferTo(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {targets.length === 0 && (
                        <option value="">No other organizations available</option>
                      )}
                      {targets.map((o) => (
                        <option key={o.id} value={o.id}>{o.name}</option>
                      ))}
                    </select>
                  )}
                </div>
              </label>

              <label className={`aivis-org-del-option aivis-org-del-option--danger ${deleteMode === 'delete_clients' ? 'aivis-org-del-option--active' : ''}`}>
                <input
                  type="radio"
                  name="org-del-mode"
                  value="delete_clients"
                  checked={deleteMode === 'delete_clients'}
                  onChange={() => setDeleteMode('delete_clients')}
                />
                <div className="aivis-org-del-option-body">
                  <span className="aivis-org-del-option-title">Delete all clients</span>
                  <span className="aivis-org-del-option-desc">
                    Permanently deletes all {org.client_count} client{org.client_count === 1 ? '' : 's'}, their prompts, runs,
                    and source data. <strong>This cannot be undone.</strong>
                  </span>
                </div>
              </label>
            </div>
          </>
        )}

        {err && <div className="aivis-auth-error" style={{ margin: '12px 0 0' }}>{err}</div>}

        <div className="aivis-wizard-actions" style={{ marginTop: 20 }}>
          <button type="button" className="aivis-btn-secondary" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="aivis-btn-danger"
            disabled={busy || (hasClients && deleteMode === 'transfer' && !transferTo)}
            onClick={handleDelete}
          >
            {busy ? 'Deleting…' : 'Delete organization'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── API Keys section ─────────────────────────────────────────── */

async function copyTextToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Fall through for browsers that deny the asynchronous clipboard API.
    }
  }
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.opacity = '0';
  document.body.appendChild(textArea);
  textArea.select();
  let copied = false;
  try {
    copied = document.execCommand('copy');
  } finally {
    document.body.removeChild(textArea);
  }
  if (!copied) throw new Error('Clipboard access was denied.');
}

function CopyButton({ text, label = 'Copy', title = 'Copy to clipboard' }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await copyTextToClipboard(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }
  return (
    <button type="button" className="aivis-apikey-copy-btn" onClick={copy} title={title}>
      {copied ? (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden>
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
          <rect x="9" y="9" width="13" height="13" rx="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      )}
      {copied ? 'Copied!' : label}
    </button>
  );
}

function PersistentKeyCopyButton({ apiKey, onError, onReveal }) {
  const [state, setState] = useState('idle');

  async function copy() {
    onError('');
    setState('loading');
    try {
      const { data } = await api.post(`/aivis/api-keys/${apiKey.id}/reveal/`);
      try {
        await copyTextToClipboard(data.raw_key);
        setState('copied');
        setTimeout(() => setState('idle'), 2000);
      } catch {
        setState('idle');
        onReveal({ name: apiKey.name, rawKey: data.raw_key });
      }
    } catch (ex) {
      setState('idle');
      onError(ex.response?.data?.detail || 'Could not copy this API key.');
    }
  }

  return (
    <button
      type="button"
      className="aivis-apikey-copy-btn aivis-datastudio-copy-btn"
      onClick={copy}
      disabled={state === 'loading'}
      title="Copy the complete API key"
    >
      {state === 'copied' ? (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden>
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
          <rect x="9" y="9" width="13" height="13" rx="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      )}
      {state === 'loading' ? 'Copying…' : state === 'copied' ? 'Copied' : 'Copy key'}
    </button>
  );
}

function DataStudioKeyRevealModal({ revealedKey, onClose }) {
  if (!revealedKey) return null;
  return (
    <div
      className="aivis-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="aivis-copy-key-title"
      onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <div className="aivis-modal aivis-datastudio-modal aivis-datastudio-copy-modal">
        <div className="aivis-datastudio-modal-heading">
          <span className="aivis-datastudio-modal-icon" aria-hidden>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <rect x="9" y="9" width="13" height="13" rx="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
          </span>
          <div>
            <h2 id="aivis-copy-key-title" className="aivis-modal-title">Copy Data Studio key</h2>
            <p>{revealedKey.name}</p>
          </div>
        </div>
        <span className="aivis-datastudio-key-label">API key</span>
        <div className="aivis-apikey-value-wrap">
          <code className="aivis-apikey-value">{revealedKey.rawKey}</code>
          <CopyButton text={revealedKey.rawKey} />
        </div>
        <p className="aivis-field-hint">
          If your browser blocks the copy button, select the key above and press ⌘C or Ctrl+C.
        </p>
        <div className="aivis-datastudio-reveal-actions">
          <button type="button" className="aivis-btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

function RevokeConfirmModal({
  keyName,
  onConfirm,
  onCancel,
  warning = 'Any automation using this key will stop working immediately.',
}) {
  if (!keyName) return null;
  return (
    <div className="aivis-modal-overlay" role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="aivis-modal aivis-org-modal" style={{ maxWidth: 420 }}>
        <div className="aivis-modal-head">
          <h2 className="aivis-modal-title">Revoke API Key</h2>
        </div>
        <p style={{ margin: '16px 0 8px', color: 'var(--aivis-text-muted)', fontSize: 14, lineHeight: 1.6 }}>
          Are you sure you want to revoke <strong style={{ color: 'var(--aivis-text, #111)' }}>{keyName}</strong>?
          <br />{warning} <strong>This cannot be undone.</strong>
        </p>
        <div className="aivis-modal-actions aivis-modal-actions--split" style={{ marginTop: 24 }}>
          <button type="button" className="aivis-btn-secondary aivis-modal-btn-cancel" onClick={onCancel}>Cancel</button>
          <button type="button" className="aivis-btn-danger aivis-modal-btn-main" onClick={onConfirm}
            style={{ flex: '0 0 70%' }}>
            Revoke key
          </button>
        </div>
      </div>
    </div>
  );
}

function NewKeyModal({ open, orgs, onClose, onCreated, onKeyReady }) {
  const [name, setName] = useState('');
  const [orgId, setOrgId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [newKey, setNewKey] = useState(null); // shown once after creation

  useEffect(() => {
    if (open) { setName(''); setOrgId(''); setErr(''); setNewKey(null); }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) { if (e.key === 'Escape' && !busy) onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  async function handleCreate() {
    if (!name.trim()) { setErr('Name is required.'); return; }
    setErr(''); setBusy(true);
    try {
      const { data } = await api.post('/aivis/api-keys/', { name: name.trim(), organization: orgId || null });
      setNewKey(data.raw_key);
      onKeyReady(data.id, data.raw_key); // store raw key in parent so table can copy it
      onCreated();                        // refresh the list in the background
    } catch (ex) {
      setErr(ex.response?.data?.detail || 'Could not create API key.');
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;
  return (
    <div className="aivis-modal-overlay" role="dialog" aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget && !busy && !newKey) onClose(); }}>
      <div className="aivis-modal aivis-org-modal">
        <div className="aivis-modal-head">
          <h2 className="aivis-modal-title">{newKey ? 'API Key Created' : 'New API Key'}</h2>
        </div>

        {newKey ? (
          <div className="aivis-apikey-reveal">
            <div className="aivis-apikey-value-wrap">
              <code className="aivis-apikey-value">{newKey}</code>
              <CopyButton text={newKey} />
            </div>
            <p className="aivis-apikey-reveal-notice">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              This key will <strong>not be shown again</strong> after you close this dialog. Copy it now and save it somewhere safe.
            </p>
            <div className="aivis-modal-actions" style={{ marginTop: 16 }}>
              <button type="button" className="aivis-btn-primary" style={{ flex: 1 }} onClick={onClose}>Done</button>
            </div>
          </div>
        ) : (
          <>
            <div className="aivis-field" style={{ marginTop: 16 }}>
              <label htmlFor="aivis-apikey-name">Key name</label>
              <input id="aivis-apikey-name" type="text" value={name}
                onChange={(e) => setName(e.target.value)} placeholder='e.g. "n8n Onboarding"'
                autoFocus disabled={busy} />
            </div>
            <div className="aivis-field">
              <label htmlFor="aivis-apikey-org">Organization (optional)</label>
              <select id="aivis-apikey-org" value={orgId} onChange={(e) => setOrgId(e.target.value)} disabled={busy}>
                <option value="">— No restriction —</option>
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
              <span className="aivis-field-hint">
                When set, this key can only create clients inside that organization.
              </span>
            </div>
            {err && <p className="aivis-auth-error" style={{ marginTop: 8 }}>{err}</p>}
            <div className="aivis-modal-actions aivis-modal-actions--split">
              <button type="button" className="aivis-btn-secondary aivis-modal-btn-cancel" onClick={onClose} disabled={busy}>Cancel</button>
              <button type="button" className="aivis-btn-primary aivis-modal-btn-main" onClick={handleCreate} disabled={busy}>
                {busy ? 'Creating…' : 'Create key'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ApiKeysSection({ orgs, isAdmin }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [revoking, setRevoking] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null); // key object pending confirmation
  // raw keys held in memory after creation — never persisted, gone on refresh
  const [recentKeys, setRecentKeys] = useState({});

  const loadKeys = useCallback(async () => {
    try {
      const { data } = await api.get('/aivis/api-keys/', { params: { page_size: 500 } });
      const list = data.results ?? data;
      setKeys(Array.isArray(list)
        ? list.filter((key) => (
          key.purpose ? key.purpose === 'automation' : !key.campaign
        ))
        : []);
    } catch { setErr('Could not load API keys.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  async function handleRevoke(key) {
    setRevokeTarget(null);
    setRevoking(key.id);
    try {
      await api.delete(`/aivis/api-keys/${key.id}/`);
      setKeys((prev) => prev.filter((k) => k.id !== key.id));
      setRecentKeys((prev) => { const next = { ...prev }; delete next[key.id]; return next; });
    } catch { setErr('Could not revoke key.'); }
    finally { setRevoking(null); }
  }

  function relativeTime(iso) {
    if (!iso) return 'Never';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 2) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  }

  return (
    <div className="aivis-settings-section">
      <div className="aivis-settings-section-head">
        <div>
          <h2 className="aivis-settings-section-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
              <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            API Keys
          </h2>
          <p className="aivis-settings-section-desc">
            Static keys for machine-to-machine access (e.g. n8n automations). Send requests with the <code>X-Api-Key</code> header.
          </p>
        </div>
        {isAdmin && (
          <button type="button" className="aivis-btn-primary aivis-settings-add-btn" onClick={() => setModalOpen(true)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
              <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
              <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
            New key
          </button>
        )}
      </div>

      {err && <p className="aivis-auth-error" style={{ marginBottom: 12 }}>{err}</p>}

      {loading ? (
        <p className="aivis-dash-empty" style={{ padding: '24px 0' }}>Loading…</p>
      ) : keys.length === 0 ? (
        <div className="aivis-apikey-empty">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden>
            <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          <p>No API keys yet.{isAdmin ? ' Create one to enable n8n integrations.' : ''}</p>
        </div>
      ) : (
        <div className="aivis-apikey-table-wrap">
          <table className="aivis-apikey-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Key prefix</th>
                <th>Organization</th>
                <th>Requests</th>
                <th>Created</th>
                <th>Last used</th>
                {isAdmin && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id}>
                  <td className="aivis-apikey-name">{k.name}</td>
                  <td><code className="aivis-apikey-prefix">{k.key_prefix}…</code></td>
                  <td>{k.organization_name || <span className="aivis-text-muted">—</span>}</td>
                  <td className="aivis-apikey-requests">{(k.request_count ?? 0).toLocaleString()}</td>
                  <td className="aivis-text-muted">{new Date(k.created_at).toLocaleDateString()}</td>
                  <td className="aivis-text-muted">{relativeTime(k.last_used_at)}</td>
                  {isAdmin && (
                    <td>
                      <div className="aivis-apikey-actions">
                        {recentKeys[k.id] && (
                          <CopyButton text={recentKeys[k.id]} label="Copy key" title="Copy full API key" />
                        )}
                        <button type="button" className="aivis-apikey-revoke-btn"
                          disabled={revoking === k.id}
                          onClick={() => setRevokeTarget(k)}>
                          {revoking === k.id ? 'Revoking…' : 'Revoke'}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <NewKeyModal
        open={modalOpen}
        orgs={orgs}
        onClose={() => setModalOpen(false)}
        onCreated={() => loadKeys()}
        onKeyReady={(id, rawKey) => setRecentKeys((prev) => ({ ...prev, [id]: rawKey }))}
      />
      <RevokeConfirmModal
        keyName={revokeTarget?.name}
        onConfirm={() => handleRevoke(revokeTarget)}
        onCancel={() => setRevokeTarget(null)}
      />
    </div>
  );
}

/* ─── Data Studio API Keys ────────────────────────────────────── */

function NewDataStudioKeyModal({ open, existingKeys, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [newKey, setNewKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open) return;
    setName('');
    setNewKey(null);
    setErr('');
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    function onKey(event) {
      if (event.key === 'Escape' && !busy && !newKey) onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy, newKey, onClose]);

  async function handleCreate() {
    if (!name.trim()) { setErr('Key name is required.'); return; }
    if (existingKeys.some((key) => key.name.trim().toLowerCase() === name.trim().toLowerCase())) {
      setErr('A Data Studio API key with this name already exists.');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      const { data } = await api.post('/aivis/api-keys/', {
        name: name.trim(),
        purpose: 'data_studio_global',
      });
      setNewKey(data.raw_key);
    } catch (ex) {
      setErr(ex.response?.data?.detail || 'Could not create Data Studio API key.');
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className="aivis-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="aivis-datastudio-key-title"
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy && !newKey) onClose();
      }}
    >
      <div className="aivis-modal aivis-datastudio-modal">
        <div className="aivis-datastudio-modal-heading">
          <span className={`aivis-datastudio-modal-icon${newKey ? ' aivis-datastudio-modal-icon--success' : ''}`} aria-hidden>
            {newKey ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <DataStudioIcon size={20} />
            )}
          </span>
          <div>
            <h2 id="aivis-datastudio-key-title" className="aivis-modal-title">
              {newKey ? 'Data Studio key created' : 'New Data Studio key'}
            </h2>
            <p>
              {newKey
                ? 'Your all-clients connector credential is ready.'
                : 'Create a separate all-clients credential for a project manager.'}
            </p>
          </div>
        </div>

        {newKey ? (
          <div className="aivis-apikey-reveal">
            <span className="aivis-datastudio-key-label">API key</span>
            <div className="aivis-apikey-value-wrap">
              <code className="aivis-apikey-value">{newKey}</code>
              <CopyButton text={newKey} />
            </div>
            <p className="aivis-apikey-reveal-notice">
              You can safely close this dialog. Admins can copy this key again from the Data Studio Keys tab.
            </p>
            <div className="aivis-datastudio-reveal-actions">
              {LOOKER_CONNECTOR_URL && (
                <a
                  className="aivis-btn-secondary aivis-datastudio-link-btn"
                  href={LOOKER_CONNECTOR_URL}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Open connector
                </a>
              )}
              <button
                type="button"
                className="aivis-btn-primary"
                onClick={() => {
                  onClose();
                  onCreated();
                }}
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="aivis-field aivis-datastudio-first-field">
              <label htmlFor="aivis-datastudio-key-name">Key name</label>
              <input
                id="aivis-datastudio-key-name"
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder='e.g. "Jeff - Looker Studio"'
                disabled={busy}
                autoFocus
              />
            </div>
            <div className="aivis-datastudio-scope-card">
              <span className="aivis-datastudio-scope-icon" aria-hidden>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M12 3a9 9 0 1 0 9 9" />
                  <path d="M12 7v5l3 2" />
                  <path d="M17 3h4v4" />
                  <path d="m21 3-5 5" />
                </svg>
              </span>
              <div>
                <strong>All clients, including future clients</strong>
                <span>
                  Give each project manager their own key so access can be revoked independently.
                </span>
              </div>
            </div>
            <p className="aivis-datastudio-security-note">
              Treat this key like a password. It can read reporting data for every active AI-Vis client.
            </p>
            {err && <p className="aivis-auth-error" style={{ marginTop: 8 }}>{err}</p>}
            <div className="aivis-modal-actions aivis-modal-actions--split">
              <button
                type="button"
                className="aivis-btn-secondary aivis-modal-btn-cancel"
                onClick={onClose}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="aivis-btn-primary aivis-modal-btn-main"
                onClick={handleCreate}
                disabled={busy}
              >
                {busy ? 'Creating…' : 'Create key'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function DataStudioKeysSection({ isAdmin, canCreateGlobalKey }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [revealedKey, setRevealedKey] = useState(null);
  const dataStudioKeys = useMemo(
    () => keys.filter((key) => (
      key.purpose
        ? key.purpose === 'data_studio_global' || key.purpose === 'data_studio_legacy'
        : Boolean(key.campaign)
    )),
    [keys],
  );

  const loadData = useCallback(async () => {
    setErr('');
    try {
      const keyResponse = await api.get('/aivis/api-keys/', { params: { page_size: 500 } });
      const keyList = keyResponse.data.results ?? keyResponse.data;
      setKeys(Array.isArray(keyList) ? keyList : []);
    } catch {
      setErr('Could not load Data Studio API keys.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  async function handleRevoke(key) {
    setRevokeTarget(null);
    setRevoking(key.id);
    try {
      await api.delete(`/aivis/api-keys/${key.id}/`);
      setKeys((current) => current.filter((item) => item.id !== key.id));
    } catch {
      setErr('Could not revoke Data Studio API key.');
    } finally {
      setRevoking(null);
    }
  }

  return (
    <section className="aivis-settings-section aivis-datastudio-section">
      <div className="aivis-settings-section-head">
        <div>
          <h2 className="aivis-settings-section-title">
            <span className="aivis-datastudio-title-mark" aria-hidden>
              <DataStudioIcon />
            </span>
            Data Studio API Keys
          </h2>
          <p className="aivis-settings-section-desc">
            Create individual all-clients keys for project managers. Each person authenticates
            once, then selects a client for every Looker Studio data source.
          </p>
        </div>
        {isAdmin && (
          <div className="aivis-datastudio-head-actions">
            <a
              className="aivis-btn-secondary aivis-datastudio-link-btn aivis-datastudio-docs-btn"
              href={LOOKER_DOCS_URL}
              target="_blank"
              rel="noreferrer noopener"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z" />
              </svg>
              View documentation
            </a>
            {LOOKER_CONNECTOR_URL && (
              <a
                className="aivis-btn-secondary aivis-datastudio-link-btn"
                href={LOOKER_CONNECTOR_URL}
                target="_blank"
                rel="noreferrer noopener"
              >
                Open connector
              </a>
            )}
            {canCreateGlobalKey && (
              <button
                type="button"
                className="aivis-btn-primary aivis-settings-add-btn"
                onClick={() => setModalOpen(true)}
              >
                New Data Studio key
              </button>
            )}
          </div>
        )}
      </div>

      {!LOOKER_CONNECTOR_URL && (
        <p className="aivis-datastudio-deploy-note">
          The connector link will appear here after the Apps Script production deployment is configured.
        </p>
      )}
      {err && <p className="aivis-auth-error" style={{ marginBottom: 12 }}>{err}</p>}

      {loading ? (
        <div className="aivis-apikey-table-skeleton" aria-label="Loading Data Studio API keys" />
      ) : dataStudioKeys.length === 0 ? (
        <div className="aivis-apikey-empty aivis-datastudio-empty">
          <p>No Data Studio keys yet.</p>
          <span>
            {canCreateGlobalKey
              ? 'Create a separate all-clients key for each project manager.'
              : 'A system admin can create the all-clients Looker Studio credential.'}
          </span>
        </div>
      ) : (
        <div className="aivis-apikey-table-wrap aivis-datastudio-table-wrap">
          <table className="aivis-apikey-table aivis-datastudio-table">
            <thead>
              <tr>
                <th>Connection</th>
                <th>Client access</th>
                <th>Activity</th>
                {isAdmin && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {dataStudioKeys.map((key) => {
                const isGlobal = key.purpose === 'data_studio_global';
                return (
                  <tr key={key.id}>
                  <td>
                    <div className="aivis-datastudio-key-identity">
                      <span className="aivis-datastudio-key-mark" aria-hidden>
                        <DataStudioIcon size={16} />
                      </span>
                      <div>
                        <div className="aivis-apikey-name">{key.name}</div>
                        <code className="aivis-apikey-prefix">{key.key_prefix}…</code>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="aivis-datastudio-client-name">
                      {isGlobal ? 'All clients' : key.campaign_name}
                      <span className={`aivis-datastudio-scope-badge${isGlobal ? '' : ' aivis-datastudio-scope-badge--legacy'}`}>
                        {isGlobal ? 'Global' : 'Legacy'}
                      </span>
                    </div>
                    <div className="aivis-datastudio-org-name">
                      {isGlobal
                        ? 'Includes current and future clients'
                        : `${key.organization_name || 'No organization'} · Client-specific key`}
                    </div>
                  </td>
                  <td>
                    <div className="aivis-datastudio-usage">
                      <span><strong>{(key.request_count ?? 0).toLocaleString()}</strong> requests</span>
                      <span>
                        {key.last_used_at
                          ? `Last used ${new Date(key.last_used_at).toLocaleDateString()}`
                          : 'Not used yet'}
                      </span>
                    </div>
                  </td>
                  {isAdmin && (
                    <td>
                      <div className="aivis-apikey-actions">
                        {key.has_recoverable_key ? (
                          <PersistentKeyCopyButton
                            apiKey={key}
                            onError={setErr}
                            onReveal={setRevealedKey}
                          />
                        ) : (
                          <span
                            className="aivis-apikey-no-copy"
                            title="This key predates secure recovery. Revoke it and create a replacement."
                          >
                            Not recoverable
                          </span>
                        )}
                        <button
                          type="button"
                          className="aivis-apikey-revoke-btn"
                          disabled={revoking === key.id}
                          onClick={() => setRevokeTarget(key)}
                        >
                          {revoking === key.id ? 'Revoking…' : 'Revoke'}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <NewDataStudioKeyModal
        open={modalOpen}
        existingKeys={dataStudioKeys}
        onClose={() => setModalOpen(false)}
        onCreated={loadData}
      />
      <RevokeConfirmModal
        keyName={revokeTarget?.name}
        onConfirm={() => handleRevoke(revokeTarget)}
        onCancel={() => setRevokeTarget(null)}
        warning="Every Looker Studio data source using this key will stop refreshing."
      />
      <DataStudioKeyRevealModal
        revealedKey={revealedKey}
        onClose={() => setRevealedKey(null)}
      />
    </section>
  );
}

/* ─── Main page ────────────────────────────────────────────────── */

const TABS = [
  { id: 'orgs',            label: 'Organizations' },
  { id: 'api-keys',        label: 'API Keys' },
  { id: 'data-studio-keys', label: 'Data Studio Keys' },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const { refreshOrgs } = useOutletContext?.() || {};

  const isAdmin = hasAdminConsoleAccess(user);
  const canCreateGlobalKey = isAdmin;
  const visibleTabs = isAdmin ? TABS : TABS.filter((tab) => tab.id === 'orgs');
  const [activeTab, setActiveTab] = useState('orgs');

  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageErr, setPageErr] = useState('');
  const [modalBusy, setModalBusy] = useState(false);

  // Add/Edit modal
  const [formOpen, setFormOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);

  // Delete modal
  const [deletingOrg, setDeletingOrg] = useState(null);

  const loadOrgs = useCallback(async () => {
    setPageErr('');
    try {
      const { data } = await api.get('/aivis/organizations/');
      const list = data.results ?? data;
      setOrgs(Array.isArray(list) ? list : []);
    } catch {
      setPageErr('Could not load organizations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOrgs(); }, [loadOrgs]);

  async function handleSaved() {
    await loadOrgs();
    if (refreshOrgs) await refreshOrgs();
  }

  function openAdd() { setEditingOrg(null); setFormOpen(true); }
  function openEdit(org) { setEditingOrg(org); setFormOpen(true); }
  function openDelete(org) { setDeletingOrg(org); }

  return (
    <div className="aivis-settings-page">
      {/* ── Page header ── */}
      <div className="aivis-settings-header">
        <div>
          <h1 className="aivis-page-title aivis-settings-title">Organizations</h1>
          <p className="aivis-page-sub">Manage organizations and API integrations.</p>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="aivis-settings-tabs" role="tablist">
        {visibleTabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={activeTab === t.id}
            className={`aivis-settings-tab${activeTab === t.id ? ' aivis-settings-tab--active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {pageErr && <div className="aivis-auth-error" style={{ marginBottom: 20 }}>{pageErr}</div>}

      {/* ── Organizations tab ── */}
      {activeTab === 'orgs' && (
        <>
          <div className="aivis-settings-tab-toolbar">
            {isAdmin && (
              <button type="button" className="aivis-btn-primary aivis-settings-add-btn" onClick={openAdd}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                  <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
                New organization
              </button>
            )}
          </div>

          {loading ? (
            <div className="aivis-org-grid">
              {[1, 2, 3].map((n) => (
                <div key={n} className="aivis-org-card aivis-org-card--skeleton" aria-hidden />
              ))}
            </div>
          ) : orgs.length === 0 ? (
            <div className="aivis-card aivis-org-empty">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M3 21V7l9-4 9 4v14"/>
                <path d="M9 21v-6h6v6"/>
              </svg>
              <p>No organizations yet.</p>
              {isAdmin && (
                <button type="button" className="aivis-btn-primary" style={{ width: 'auto', marginTop: 8 }} onClick={openAdd}>
                  Create your first organization
                </button>
              )}
            </div>
          ) : (
            <div className="aivis-org-grid">
              {orgs.map((org) => (
                <OrgCard
                  key={org.id}
                  org={org}
                  isAdmin={isAdmin}
                  onEdit={() => openEdit(org)}
                  onDelete={() => openDelete(org)}
                />
              ))}
            </div>
          )}

          <OrgFormModal
            open={formOpen}
            org={editingOrg}
            onClose={() => setFormOpen(false)}
            onSaved={handleSaved}
            busy={modalBusy}
            setBusy={setModalBusy}
          />

          <OrgDeleteModal
            org={deletingOrg}
            orgs={orgs}
            onClose={() => setDeletingOrg(null)}
            onDeleted={handleSaved}
            busy={modalBusy}
            setBusy={setModalBusy}
          />
        </>
      )}

      {/* ── API Keys tab ── */}
      {activeTab === 'api-keys' && (
        <ApiKeysSection orgs={orgs} isAdmin={isAdmin} />
      )}

      {/* ── Data Studio Keys tab ── */}
      {activeTab === 'data-studio-keys' && isAdmin && (
        <DataStudioKeysSection
          isAdmin
          canCreateGlobalKey={canCreateGlobalKey}
        />
      )}
    </div>
  );
}

/* ─── Org card ─────────────────────────────────────────────────── */

function OrgCard({ org, isAdmin, onEdit, onDelete }) {
  const hostname = displayHostname(org.website);
  const clientCount = org.client_count ?? 0;

  return (
    <div className="aivis-org-card">
      <div className="aivis-org-card-top">
        <OrgAvatar org={org} />
        <div className="aivis-org-card-info">
          <h3 className="aivis-org-card-name">{org.name}</h3>
          {hostname ? (
            <a
              href={org.website}
              target="_blank"
              rel="noreferrer noopener"
              className="aivis-org-card-website"
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                <circle cx="12" cy="12" r="10"/>
                <line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
              {hostname}
            </a>
          ) : (
            <span className="aivis-org-card-website aivis-org-card-website--none">No website</span>
          )}
        </div>
      </div>

      <div className="aivis-org-card-stats">
        <div className="aivis-org-card-stat">
          <span className="aivis-org-card-stat-value">{clientCount}</span>
          <span className="aivis-org-card-stat-label">Client{clientCount === 1 ? '' : 's'}</span>
        </div>
        <div className="aivis-org-card-stat">
          <span className="aivis-org-card-stat-value">
            {new Date(org.created_at).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}
          </span>
          <span className="aivis-org-card-stat-label">Created</span>
        </div>
      </div>

      {isAdmin && (
        <div className="aivis-org-card-actions">
          <button type="button" className="aivis-org-card-btn" onClick={onEdit}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5Z"
                stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Edit
          </button>
          <button type="button" className="aivis-org-card-btn aivis-org-card-btn--danger" onClick={onDelete}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M3 6h18M8 6V4h8v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M10 11v6M14 11v6"
                stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
            Delete
          </button>
        </div>
      )}
    </div>
  );
}
